import { type JSX, useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom-7"

import { Message, Spinner } from "~/shared/ui/kit"
import {
	generateAuthUrlFor,
	UserAccessRestrictionCode,
} from "~/domain/user-session"
import { dec, delay } from "~/shared/lib/utils"
import { HSpace } from "~/shared/ui"

import { logout } from "../api/logout"

const TIME_TO_LOGOUT = 5
const TIME_TO_REDIRECT = 5

function DefaultSessionExpiredMessage(): JSX.Element {
	const [timeToLogout, setTimeToLogout] = useState(TIME_TO_LOGOUT)

	useEffect(() => {
		;(async () => {
			for (let i = 0; i < TIME_TO_LOGOUT; ++i) {
				await delay(1000)
				setTimeToLogout(dec)
			}

			await logout()
		})()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<>
			Сессия истекла! Выход из системы через {timeToLogout} сек.{" "}
			<Spinner scale={1} style={{ width: "1rem" }} />
		</>
	)
}

function DefaultLoginIsRequiredMessage() {
	const [timeToRedirect, setTimeToRedirect] = useState(TIME_TO_REDIRECT)
	const navigate = useNavigate()

	useEffect(() => {

		let unmounted = false

		;(async () => {
			for (let i = 0; i < TIME_TO_LOGOUT; ++i) {
				await delay(1000)

				if(unmounted) return

				setTimeToRedirect(dec)
			}

			navigate(generateAuthUrlFor())
		})()

		return () => {
			unmounted = true
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<>
			<div>
				Для доступа к данной странице требуется{" "}
				<Link to={generateAuthUrlFor()}>войти в систему!</Link>
			</div>
			<div className="flex items-center">
				{" "}
				<div>
					Переход на главную страницу через {timeToRedirect} сек.
				</div>
				<HSpace size={1} />
				<Spinner scale={1} style={{ width: "1rem" }} />
			</div>
		</>
	)
}

/**
 * Представляет тексты сообщений, которые выводятся при ошибке авторизации
 */
export interface RestrictedMessages {
	/** сообщение об отсутствии доступа */
	noAccessMessage?: JSX.Element | string | null
	/** сообщение о необходимости войти в систему */
	loginIsRequiredMessage?: JSX.Element | string | null
	/** сообщение об истечении сессии */
	sessionExpiredMessage?: JSX.Element | string | null
}

function getMessageByCode(
	code: UserAccessRestrictionCode,
	{
		noAccessMessage = null,
		loginIsRequiredMessage = null,
		sessionExpiredMessage = null,
	}: RestrictedMessages = {},
) {
	switch (code) {
		case UserAccessRestrictionCode.LOGIN_IS_REQUIRED:
			return loginIsRequiredMessage || <DefaultLoginIsRequiredMessage />

		case UserAccessRestrictionCode.PASSWORD_CHANGE_IS_REQUIRED:
			return (
				<>
					Перед началом работы необходимо {" "}
					<Link to="/main/profile/change-password">
						сменить пароль
					</Link>
					!
				</>
			)

		case UserAccessRestrictionCode.NO_ACCESS:
			return (
				noAccessMessage || "У Вас отсутствует доступ к данной странице!"
			)

		case UserAccessRestrictionCode.SESSION_EXPIRED:
			return sessionExpiredMessage || <DefaultSessionExpiredMessage />

		default:
			return null
	}
}

export function AccessErrorMessage({
	code,
	errorMessage,
}: {
	code: UserAccessRestrictionCode
	errorMessage?: string | JSX.Element
}) {
	const message = errorMessage || getMessageByCode(code)

	return (
		<Message type="error">
			{message}
		</Message>
	)
}
